import React, { useEffect, useRef } from 'react';
import useDevice from '../../useDevice';
import Button from '../Button/Button';

export interface AccessibleDropdownToggleProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * The element type or component to render.
   * Can be a string like 'button', 'div', or a React component.
   */
  as?: React.ElementType;
  [key: string]: unknown;
}

/**
 * Wraps any element to fix iOS VoiceOver aria-expanded announcement issues.
 * Temporarily removes and restores the aria-expanded attribute on click to
 * force VoiceOver to re-announce the toggle state.
 *
 * @author Victor Ige
 */
const AccessibleDropdownToggle = React.forwardRef<HTMLElement, AccessibleDropdownToggleProps>(
  ({ as: Component = Button, onClick, ...props }, forwardedRef) => {
    const internalRef = useRef<HTMLElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const { isIos, isMac } = useDevice();

    // Remove aria-haspopup from props on iOS and Mac only (to avoid conflicts with VoiceOver)
    const { 'aria-haspopup': ariaHasPopup, ...propsWithoutHasPopup } =
      props as React.HTMLAttributes<HTMLElement>;
    const restProps = isIos || isMac ? propsWithoutHasPopup : props;

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
      // Call original onClick handler
      onClick?.(event);

      // Only apply the fix on iOS devices
      if (!isIos || !internalRef.current) {
        return;
      }

      const element = internalRef.current;

      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Wait for React to update aria-expanded to the new value
      requestAnimationFrame(() => {
        const value = element.getAttribute('aria-expanded');

        // Remove aria-expanded to force VoiceOver to re-read it
        element.removeAttribute('aria-expanded');

        // Restore after 100ms delay
        timeoutRef.current = setTimeout(() => {
          if (value !== null) {
            element.setAttribute('aria-expanded', value);
          }
        }, 100);
      });
    };

    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...restProps}
        ref={(node: HTMLElement) => {
          // Store internal ref
          internalRef.current = node;

          // Forward ref to parent
          if (typeof forwardedRef === 'function') {
            forwardedRef(node);
          } else if (forwardedRef) {
            forwardedRef.current = node;
          }
        }}
        onClick={handleClick}
      />
    );
  }
);

AccessibleDropdownToggle.displayName = 'AccessibleDropdownToggle';

export default AccessibleDropdownToggle;
